/**
 * Vault → Send (Phase 9b, D65–D67).
 *   1. Form: network, asset, recipient (or "My Account" — your own custodial deposit address), amount.
 *   2. Review: the server supplies network details; the DEVICE works out and caps the fee.
 *   3. Vault password again (every send), then the transaction is signed here, in the browser,
 *      and only the signed transaction is sent to the API to broadcast.
 *   4. Live status (pending → confirmed) with an explorer link; remembered in "Recent vault sends".
 * The signing code (@custos/vault/send) is loaded only when this screen needs it.
 */
import type { AssetInfo, ChainInfo } from "@custos/api-client";
import { APP_SHORT_NAME, formatUnits, parseUnits, type ChainKey } from "@custos/core";
import {
  Button,
  Card,
  CardTitle,
  Field,
  Input,
  Notice,
  Select,
  SuccessCheck,
  formatAmount,
  normaliseAmountInput,
} from "@custos/ui";
import type { VaultFeeQuote } from "@custos/vault/send";
import { useQuery } from "@tanstack/react-query";
import { UserRound } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { PageHeader } from "../components/AppShell";
import { ExternalLink, Row } from "../components/bits";
import { SecretInput } from "../components/VaultBits";
import { api } from "../lib/api";
import {
  addressPlaceholder,
  explorerTxUrl,
  FAMILY_KEY,
  findChain,
  looksLikeAddress,
  networkLabel,
} from "../lib/chains";
import { errorMessage, isApiError } from "../lib/errors";
import { usePrefs } from "../lib/prefs";
import { useConfig, useVaultBalances } from "../lib/queries";
import { revealVaultSecret } from "../lib/vault";
import { useVaultSession } from "../lib/vault-ui";
import { addVaultSend } from "../lib/vault-sends";

const loadSigner = () => import("@custos/vault/send");

interface Draft {
  chain: ChainInfo;
  asset: AssetInfo;
  from: string;
  to: string;
  amount: string; // human
  units: bigint;
}

interface Review {
  draft: Draft;
  fee: VaultFeeQuote;
}

/** Shown when the user has to look again (e.g. the fee went up while they were typing the password). */
const FEE_RISE_LIMIT_PERCENT = 125n;

export function VaultSendPage() {
  const config = useConfig();
  const vault = useVaultSession();
  const currency = usePrefs((s) => s.currency);
  const balances = useVaultBalances(vault.accounts, currency, vault.status === "unlocked");
  const [params, setParams] = useSearchParams();
  const [review, setReview] = useState<Review | null>(null);
  const [sent, setSent] = useState<{ draft: Draft; txHash: string } | null>(null);

  if (vault.status !== "unlocked" && vault.status !== "loading") return <Navigate to="/vault" replace />;
  if (!config.data) return null;
  const mode = config.data.networkMode;

  if (sent) return <SentView chain={sent.draft.chain} draft={sent.draft} txHash={sent.txHash} />;

  const chains = config.data.chains.filter((c) =>
    vault.accounts.some((a) => a.family === FAMILY_KEY[c.family]),
  );
  const chain = findChain(chains, params.get("chain") ?? "") ?? chains[0];
  if (!chain) return null;
  const assets = config.data.assets.filter((a) => a.chain === chain.key);
  const asset = assets.find((a) => a.symbol === params.get("asset")) ?? assets[0]!;
  const from = vault.accounts.find((a) => a.family === FAMILY_KEY[chain.family])!.address;
  const balanceOf = (symbol: string) =>
    balances.data?.assets.find((r) => r.chain === chain.key && r.asset === symbol)?.baseUnits ?? null;
  const native = assets.find((a) => a.isNative)!;

  return (
    <>
      <PageHeader title="Send from vault" subtitle="Signed on this device with your vault key." />
      <div className="mx-auto flex max-w-xl flex-col gap-4">
        {review ? (
          <ReviewStep
            review={review}
            mode={mode}
            nativeBalance={balanceOf(native.symbol)}
            onBack={() => setReview(null)}
            onSent={(txHash) => {
              addVaultSend(mode, {
                chain: review.draft.chain.key,
                asset: review.draft.asset.symbol,
                amount: review.draft.amount,
                to: review.draft.to,
                txHash,
                at: new Date().toISOString(),
              });
              setSent({ draft: review.draft, txHash });
            }}
          />
        ) : (
          <FormStep
            key={`${chain.key}:${asset.symbol}`}
            chains={chains}
            chain={chain}
            assets={assets}
            asset={asset}
            from={from}
            mode={mode}
            balance={balanceOf(asset.symbol)}
            onPick={(c, a) => setParams(a ? { chain: c, asset: a } : { chain: c }, { replace: true })}
            onReview={setReview}
          />
        )}
      </div>
    </>
  );
}

function FormStep(props: {
  chains: ChainInfo[];
  chain: ChainInfo;
  assets: AssetInfo[];
  asset: AssetInfo;
  from: string;
  mode: "testnet" | "mainnet";
  balance: string | null;
  onPick: (chain: string, asset?: string) => void;
  onReview: (review: Review) => void;
}) {
  const { chain, asset, mode, from, balance } = props;
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [accountNote, setAccountNote] = useState<string | null>(null);

  const human = normaliseAmountInput(amount, asset.decimals);
  const units = human ? parseUnits(human, asset.decimals) : 0n;
  const addressOk = looksLikeAddress(chain.family, to, mode);
  const tooMuch = balance !== null && units > BigInt(balance);
  const canReview = addressOk && units > 0n && !tooMuch;

  const fillMyAccount = async () => {
    setAccountNote(null);
    try {
      const info = await api.depositAddress(FAMILY_KEY[chain.family]);
      setTo(info.address);
      setAccountNote(
        `Your ${APP_SHORT_NAME} Account deposit address. It's credited after the usual confirmations.`,
      );
    } catch (err) {
      setAccountNote(
        isApiError(err, "DEPOSITS_UNAVAILABLE")
          ? "Your Account address can't be shown right now (the signing service is offline)."
          : errorMessage(err),
      );
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canReview || !human) return;
    setBusy(true);
    setError(null);
    try {
      const draft: Draft = { chain, asset, from, to: to.trim(), amount: human, units };
      const { fee } = await quote(draft, mode);
      props.onReview({ draft, fee });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="animate-rise">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Network">
            {(p) => (
              <Select {...p} value={chain.key} onChange={(e) => props.onPick(e.target.value)}>
                {props.chains.map((c) => (
                  <option key={c.key} value={c.key}>
                    {networkLabel(c)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Asset">
            {(p) => (
              <Select {...p} value={asset.symbol} onChange={(e) => props.onPick(chain.key, e.target.value)}>
                {props.assets.map((a) => (
                  <option key={a.symbol} value={a.symbol}>
                    {a.symbol}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Field
          label="Recipient address"
          error={
            touched && to.trim() && !addressOk
              ? `This isn't a ${networkLabel(chain)} address. Check you picked the right network.`
              : null
          }
          hint={accountNote ?? `Only a ${networkLabel(chain)} address.`}
        >
          {(p) => (
            <Input
              {...p}
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setAccountNote(null);
              }}
              onBlur={() => setTouched(true)}
              placeholder={addressPlaceholder(chain.family, mode)}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
            />
          )}
        </Field>
        <button
          type="button"
          onClick={() => void fillMyAccount()}
          className="-mt-2 inline-flex min-h-11 items-center gap-2 self-start rounded-full px-3 text-sm text-primary-strong hover:bg-hover"
        >
          <UserRound className="size-4" aria-hidden /> Send to my Account
        </button>
        <Field
          label={`Amount (${asset.symbol})`}
          error={touched && tooMuch ? "That's more than the vault holds." : null}
          hint={
            balance === null
              ? "Balance unknown right now."
              : `Available: ${formatAmount(formatUnits(BigInt(balance), asset.decimals), asset.decimals)} ${asset.symbol}`
          }
        >
          {(p) => (
            <div className="flex gap-2">
              <Input
                {...p}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                className="font-mono"
              />
              {!asset.isNative && balance !== null && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setAmount(formatUnits(BigInt(balance), asset.decimals))}
                >
                  Max
                </Button>
              )}
            </div>
          )}
        </Field>
        {!asset.isNative && (
          <p className="-mt-2 text-xs text-muted">
            The network fee is paid in {props.assets.find((a) => a.isNative)?.symbol} from this vault.
          </p>
        )}
        {error !== null && <Notice tone="danger">{errorMessage(error)}</Notice>}
        <Button type="submit" block loading={busy} disabled={!to.trim() || !human}>
          Review
        </Button>
        <Link to="/vault" className="self-center text-sm text-muted hover:text-fg hover:underline">
          Cancel
        </Link>
      </form>
    </Card>
  );
}

/** Fresh network details from the server, then the device's own fee check. */
async function quote(draft: Draft, mode: "testnet" | "mainnet") {
  const prepared = await api.vaultPrepareSend({
    chain: draft.chain.key,
    asset: draft.asset.symbol,
    from: draft.from,
    to: draft.to,
    amount: draft.units.toString(),
  });
  const { quoteVaultFee } = await loadSigner();
  const req = {
    chain: draft.chain.key as ChainKey,
    asset: draft.asset.symbol,
    to: draft.to,
    amount: draft.units,
  };
  return { fee: quoteVaultFee(mode, req, prepared.params), params: prepared.params, req };
}

function ReviewStep({
  review,
  mode,
  nativeBalance,
  onBack,
  onSent,
}: {
  review: Review;
  mode: "testnet" | "mainnet";
  nativeBalance: string | null;
  onBack: () => void;
  onSent: (txHash: string) => void;
}) {
  const { draft } = review;
  const [fee, setFee] = useState(review.fee);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const feeText = `${fee.exact ? "" : "up to "}${formatAmount(formatUnits(fee.maxFee, fee.nativeDecimals), fee.nativeDecimals)} ${fee.nativeSymbol}`;
  const needNative = fee.maxFee + (draft.asset.isNative ? draft.units : 0n);
  const short = nativeBalance !== null && BigInt(nativeBalance) < needNative;

  const send = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // Fresh details (blockhashes and Tron block refs expire within a minute).
      const fresh = await quote(draft, mode);
      if (fresh.fee.maxFee * 100n > fee.maxFee * FEE_RISE_LIMIT_PERCENT) {
        setFee(fresh.fee);
        setNotice("The network fee went up. Check the new fee, then send again.");
        return;
      }
      // Password again for every send (D66): decrypts the vault from storage, not from memory.
      const secret = await revealVaultSecret(password);
      setPassword("");
      const { signVaultSend } = await loadSigner();
      const signed = await signVaultSend(secret, mode, fresh.req, fresh.params);
      await api.vaultBroadcast({ chain: draft.chain.key, signedTx: signed.signedTx, txHash: signed.txHash });
      onSent(signed.txHash);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="animate-rise">
      <form onSubmit={send} className="flex flex-col gap-4" noValidate>
        <CardTitle>Review</CardTitle>
        <div className="flex flex-col gap-2 text-sm" data-testid="vault-send-review">
          <Row label="Network">{networkLabel(draft.chain)}</Row>
          <Row label="Amount" strong>
            <span className="font-mono">
              {formatAmount(draft.amount, draft.asset.decimals)} {draft.asset.symbol}
            </span>
          </Row>
          <Row label="Network fee">
            <span className="font-mono" data-testid="vault-send-fee">
              {feeText}
            </span>
          </Row>
          <div className="mt-1 rounded-field border border-line bg-soft p-3">
            <p className="text-xs text-muted">To</p>
            <code className="block font-mono text-sm break-all text-fg" data-testid="vault-send-to">
              {draft.to}
            </code>
          </div>
        </div>
        <Notice tone="warning">
          Blockchain transfers can't be undone. Check the address and the network ({draft.chain.network}).
        </Notice>
        {short && (
          <Notice tone="danger">
            Not enough {fee.nativeSymbol} in the vault for {draft.asset.isNative ? "the amount plus " : ""}the
            network fee.
          </Notice>
        )}
        {notice && <Notice tone="info">{notice}</Notice>}
        <Field label="Vault password" hint="Needed for every send.">
          {(p) => (
            <SecretInput
              {...p}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          )}
        </Field>
        {error !== null && <Notice tone="danger">{sendErrorMessage(error)}</Notice>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button type="button" variant="secondary" onClick={onBack} disabled={busy}>
            Back
          </Button>
          <Button type="submit" className="flex-1" loading={busy} disabled={!password || short}>
            Sign and send
          </Button>
        </div>
      </form>
    </Card>
  );
}

function sendErrorMessage(error: unknown): string {
  if (error instanceof Error && error.name === "VaultSendError") return error.message;
  return errorMessage(error);
}

function SentView({ chain, draft, txHash }: { chain: ChainInfo; draft: Draft; txHash: string }) {
  const status = useQuery({
    queryKey: ["vault-tx", chain.key, txHash],
    queryFn: () => api.vaultTxStatus(chain.key, txHash),
    refetchInterval: (q) =>
      q.state.data?.status === "confirmed" || q.state.data?.status === "failed" ? false : 5_000,
  });
  const s = status.data;
  const label =
    s?.status === "confirmed"
      ? "Confirmed"
      : s?.status === "failed"
        ? "Failed on the network"
        : s && s.confirmations > 0
          ? `Confirming (${s.confirmations}/${s.required})`
          : "Waiting for the network…";
  return (
    <>
      <PageHeader title="Sent" />
      <Card className="animate-rise mx-auto flex max-w-xl flex-col items-center gap-4 text-center">
        <SuccessCheck />
        <p className="text-lg text-fg">
          {formatAmount(draft.amount, draft.asset.decimals)} {draft.asset.symbol} sent on{" "}
          {networkLabel(chain)}
        </p>
        <p className="text-sm text-muted" data-testid="vault-send-status">
          {label}
        </p>
        <code className="max-w-full font-mono text-xs break-all text-muted" data-testid="vault-send-hash">
          {txHash}
        </code>
        <ExternalLink href={explorerTxUrl(chain, txHash)}>View on explorer</ExternalLink>
        <Link to="/vault" className="text-sm text-primary-strong hover:underline">
          Back to vault
        </Link>
      </Card>
    </>
  );
}
